import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { space, device } from '@ticketswap/solar'
import { useViewer } from '../ViewerContext'
import { useLanguage } from '../LanguageContext'
import { track } from '~/utils/analytics'
import { APP_DOWNLOAD_CLICKED } from '~/lib/tracking-events'

const List = styled.ul`
  display: flex;
  flex-direction: column;

  @media ${device.tablet} {
    flex-direction: row;
  }

  li:not(:first-of-type) {
    margin-top: ${space[8]};

    @media ${device.tablet} {
      margin-top: 0;
      margin-left: ${space[16]};
    }
  }

  img {
    vertical-align: middle;
    max-width: 100%;
    max-height: ${space[48]};
  }
`

const Image = styled.img`
  height: 60px;
`

export function AppStoreBadge() {
  const language = useLanguage()

  function getImageUrl() {
    switch (language) {
      case 'de':
        return '/static/images/appStore/app-store-de.svg'
      case 'es':
        return '/static/images/appStore/app-store-es.svg'
      case 'fr':
        return '/static/images/appStore/app-store-fr.svg'
      case 'hu':
        return '/static/images/appStore/app-store-hu.svg'
      case 'nl':
        return '/static/images/appStore/app-store-nl.svg'
      case 'pt':
        return '/static/images/appStore/app-store-pt.svg'
      default:
        return '/static/images/appStore/app-store-en.svg'
    }
  }

  return <Image src={getImageUrl()} loading="lazy" alt="App Store" />
}

export function GooglePlayBadge() {
  const language = useLanguage()

  function getImageUrl() {
    switch (language) {
      case 'de':
        return '/static/images/appStore/google-play-de.svg'
      case 'es':
        return '/static/images/appStore/google-play-es.svg'
      case 'fr':
        return '/static/images/appStore/google-play-fr.svg'
      case 'hu':
        return '/static/images/appStore/google-play-hu.svg'
      case 'nl':
        return '/static/images/appStore/google-play-nl.svg'
      case 'pt':
        return '/static/images/appStore/google-play-pt.svg'
      default:
        return '/static/images/appStore/google-play-en.svg'
    }
  }

  return <Image src={getImageUrl()} loading="lazy" alt="Google Play" />
}

export function AppStoreLink({ children, trackingContext, ...props }) {
  return (
    <a
      href={`https://apps.apple.com/app/apple-store/id932337449?pt=76205801&ct=${encodeURIComponent(
        trackingContext.toLowerCase()
      )}&mt=8`}
      onClick={() =>
        track(APP_DOWNLOAD_CLICKED, {
          store: 'App Store',
          trigger: trackingContext,
        })
      }
      {...props}
    >
      {children}
    </a>
  )
}

AppStoreLink.defaultProps = {
  trackingContext: 'General',
}

export function GooglePlayLink({ children, trackingContext, ...props }) {
  return (
    <a
      href={`https://play.google.com/store/apps/details?id=com.ticketswap.ticketswap&referrer=utm_source%ticketswap%26utm_medium%3Dinternal%26utm_term%3D${encodeURIComponent(
        trackingContext.toLowerCase()
      )}`}
      onClick={() =>
        track(APP_DOWNLOAD_CLICKED, {
          store: 'Google Play',
          trigger: trackingContext,
        })
      }
      {...props}
    >
      {children}
    </a>
  )
}

GooglePlayLink.defaultProps = {
  trackingContext: 'General',
}

function AppStoreBadges({ onClick, trackingContext, ...props }) {
  const { deviceInfo } = useViewer()
  const { isDesktop, isMobile, isIOS, isApp, isAndroid } = deviceInfo
  const showAppStoreLink = isDesktop || ((isMobile || isApp) && isIOS)
  const showGooglePlayLink = isDesktop || ((isMobile || isApp) && isAndroid)

  return (
    <List {...props}>
      {showAppStoreLink && (
        <li>
          <AppStoreLink trackingContext={trackingContext}>
            <AppStoreBadge />
          </AppStoreLink>
        </li>
      )}
      {showGooglePlayLink && (
        <li>
          <GooglePlayLink trackingContext={trackingContext}>
            <GooglePlayBadge />
          </GooglePlayLink>
        </li>
      )}
    </List>
  )
}

AppStoreBadges.defaultProps = {
  onClick: () => {},
  trackingContext: 'General',
}

AppStoreBadges.propTypes = {
  onClick: PropTypes.func,
  trackingContext: PropTypes.string,
}

export default AppStoreBadges
